import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import {
  FiCode, FiCheck, FiList, FiGlobe, FiMessageSquare,
  FiPackage, FiDownloadCloud, FiExternalLink,
} from 'react-icons/fi';
import Reel from '../components/Reel';
import CleanVideo from '../components/CleanVideo';
import { getProduct } from '../data/catalog';

const SITE_LINK = 'https://crxify.com';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="group border border-border bg-card p-6 transition-colors hover:border-signal/50">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-signal/10 transition-transform group-hover:scale-110">
      <SafeIcon icon={icon} className="h-6 w-6 text-signal" />
    </div>
    <h3 className="mb-2 font-display text-xl font-bold text-white">{title}</h3>
    <p className="text-sm leading-relaxed text-muted">{desc}</p>
  </div>
);

const SpecItem = ({ title, value }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border py-3">
    <span className="shrink-0 text-sm text-muted">{title}</span>
    <span className="text-right font-mono text-sm text-primary">{value}</span>
  </div>
);

const Crxify = () => {
  const PRODUCT = getProduct('crxify');

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="border-b border-border pb-16 pt-32">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-signal/20 bg-signal/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.16em] text-signal">
              <SafeIcon icon={FiCode} /> Hosted web app
            </div>
            <h1 className="mb-6 font-display text-5xl font-extrabold tracking-tightest text-white md:text-7xl">
              Turn a sentence into<br />
              <span className="text-signal">a real Chrome extension.</span>
            </h1>
            <p className="mx-auto mb-10 max-w-3xl text-xl leading-relaxed text-muted">
              Crxify is our web app that builds working Chrome extensions from a plain-language
              description. No coding, no boilerplate, no manifest files — describe it, preview it,
              and load it straight into Chrome.
            </p>
            <div className="flex flex-col items-center justify-center gap-5 sm:flex-row">
              <a
                href={SITE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary bg-signal px-10 py-4 font-semibold text-background hover:bg-signal/90"
              >
                <SafeIcon icon={FiGlobe} className="h-5 w-5" />
                Open crxify.com
              </a>
              <div className="flex flex-col items-start text-left">
                <span className="font-mono font-bold text-white">{PRODUCT.price}</span>
                <span className="text-xs text-muted">Runs in your browser · nothing to install</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trailer */}
      <section className="border-b border-border py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <p className="rule-label mb-6">Trailer — Crxify</p>
          <CleanVideo src="crxify.mp4" title="Crxify Trailer" />
        </div>
      </section>

      {/* Reel */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — Crxify</p>
          <Reel frames={PRODUCT.frames} label="Crxify" sound={false} ratio="aspect-[16/10]" />
        </div>
      </section>

      {/* What you get */}
      <section className="border-y border-border bg-card/30 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold text-white">From idea to installable extension</h2>
            <p className="text-muted">Everything you need to go from a sentence to a working Chrome extension.</p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <FeatureCard
              icon={FiMessageSquare}
              title="Describe it"
              desc="Type what you want your extension to do in plain English — no technical spec required."
            />
            <FeatureCard
              icon={FiCode}
              title="AI generates the code"
              desc="Crxify writes the manifest, scripts, and UI for a fully working Chrome extension automatically."
            />
            <FeatureCard
              icon={FiPackage}
              title="Preview and refine"
              desc="Test the generated extension and ask for changes in natural language until it is exactly right."
            />
            <FeatureCard
              icon={FiDownloadCloud}
              title="Load and ship"
              desc="Download the extension package and load it straight into Chrome, ready to use or publish."
            />
          </div>
        </div>
      </section>

      {/* How it works + specs */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-20 lg:flex-row">
            <div className="flex-1 space-y-8">
              <h2 className="font-display text-4xl font-bold leading-tight text-white">
                Build extensions<br />
                <span className="text-signal">without writing code.</span>
              </h2>
              <p className="text-muted">
                Crxify handles the manifest, the logic, and the packaging. You describe what the
                extension should do; it comes back as something you can load into Chrome and use.
              </p>
              <div className="space-y-6">
                {[
                  ['Describe your idea', 'Tell Crxify what your extension should do, in a sentence or a short paragraph.'],
                  ['AI builds it', 'Crxify generates the manifest, logic, and interface needed for a real Chrome extension.'],
                  ['Iterate instantly', 'Ask for tweaks — colors, behaviour, new features — and watch the extension update.'],
                  ['Load into Chrome', 'Download the package and load it as an unpacked extension, or prepare it for the Web Store.'],
                ].map(([t, d]) => (
                  <div key={t} className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-signal/10 text-signal">
                      <SafeIcon icon={FiCheck} />
                    </div>
                    <p className="text-muted">
                      <strong className="text-white">{t}:</strong> {d}
                    </p>
                  </div>
                ))}
              </div>
              <a
                href={SITE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-medium text-white transition-colors hover:text-signal"
              >
                See it live on crxify.com <SafeIcon icon={FiExternalLink} />
              </a>
            </div>

            <div className="w-full max-w-md flex-1">
              <div className="border border-border bg-card p-8">
                <h3 className="mb-6 flex items-center gap-2 font-display text-xl font-bold text-white">
                  <SafeIcon icon={FiList} className="text-signal" /> At a glance
                </h3>
                <div className="space-y-1">
                  <SpecItem title="Type" value="Hosted web app" />
                  <SpecItem title="Platform" value="Browser — nothing to install" />
                  <SpecItem title="Output" value="Real, loadable Chrome extension" />
                  <SpecItem title="Editing" value="Natural-language iteration" />
                  <SpecItem title="Pricing" value="Free to start" />
                </div>
                <div className="mt-8 border-t border-border pt-6">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="font-bold text-white">Launch price</span>
                    <span className="font-mono text-xl font-bold text-signal">{PRODUCT.price}</span>
                  </div>
                  <p className="mb-6 text-xs text-muted">
                    Current pricing and plans always live on crxify.com.
                  </p>
                  <a
                    href={SITE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center justify-center gap-2 bg-white py-3 font-bold text-background transition-colors hover:bg-gray-200"
                  >
                    Launch Crxify
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-24 text-center">
        <div className="absolute inset-0 -z-10 bg-signal/5" />
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="mb-6 font-display text-4xl font-bold text-white">
            Your idea, shipped as an extension.
          </h2>
          <p className="mb-10 text-muted">
            Describe it once. Crxify handles the manifest, the code, and the packaging.
          </p>
          <a
            href={SITE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary mx-auto w-fit bg-signal px-12 py-5 font-semibold text-background hover:bg-signal/90"
          >
            <SafeIcon icon={FiGlobe} className="h-5 w-5" />
            Open crxify.com
          </a>
        </div>
      </section>
    </div>
  );
};

export default Crxify;
